import { Eye, Mail, MoreHorizontal, Plus, Trash2, UserX } from "lucide-react";
import { useState, type FC, type ReactNode } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import { portalPreviewUrl } from "../../companyPortal/preview";
import { companyActions } from "../actions";
import { PortalFirmAPI } from "../api/companiesApi";
import { refreshCompanies, useContactLogins } from "../queries";
import {
  CONTACT_ROLES,
  PORTAL_ACCESS,
  type Company,
  type CompanyContact,
  type CompanyPortalSwitches,
  type ContactLogin,
  type ContactRole,
  type PortalAccess
} from "../types";
import { ACCESS_LABEL, initialsOf, ROLE_LABEL } from "../utils";

const COLS =
  "grid grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,1fr)_200px_64px_32px] items-center gap-3 px-4";
const ALL_NOTICES = ["filed", "approved", "rfe", "expiring"];
const AUTO_APPROVER = "__roles__";

const PORTAL_SWITCHES: Array<{
  key: Exclude<keyof CompanyPortalSwitches, "approver_contact_id">;
  title: string;
  desc: string;
}> = [
  {
    key: "requests",
    title: "Request new cases",
    desc: "Requests go to the approver, then to you to accept."
  },
  {
    key: "docs",
    title: "See documents, forms and USCIS notices",
    desc: "Per employee, for contacts with access to that employee."
  },
  {
    key: "spend",
    title: "See spend against budget",
    desc: "The company's share only, by cost center and employee. Needs time tracking on. No invoices or payment status."
  },
  {
    key: "messages",
    title: "Message the firm",
    desc: "Threads land on the case and in the responsible attorney's inbox."
  }
];

const shortDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric"
      })
    : "";

/** "Signed in Oct 8", "Invited Oct 7", "Not invited". */
export const loginLabel = (login: ContactLogin | undefined): string => {
  if (!login || login.status === "revoked") return "Not invited";
  if (login.status === "active")
    return login.last_seen_at
      ? `Signed in ${shortDate(login.last_seen_at)}`
      : "Signed in";
  return `Invited ${shortDate(login.invited_at)}`.trim();
};

const runPortal = async (run: () => Promise<unknown>, done: string) => {
  try {
    await run();
    toast(done);
  } catch (error) {
    toast.error(
      error instanceof Error ? error.message : "Couldn't update the portal"
    );
  } finally {
    void refreshCompanies();
  }
};

const AddContactDialog: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (c: Partial<CompanyContact>) => Promise<unknown>;
}> = ({ open, onOpenChange, onAdd }) => {
  const [form, setForm] = useState({
    name: "",
    title: "",
    email: "",
    phone: ""
  });
  const set =
    (k: keyof typeof form) => (e: { currentTarget: { value: string } }) => {
      const { value } = e.currentTarget;
      setForm((f) => ({ ...f, [k]: value }));
    };
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    const saved = await onAdd({
      ...form,
      roles: ["primary"],
      portal_access: "none",
      notices: []
    }).finally(() => setBusy(false));
    if (!saved) return;
    setForm({ name: "", title: "", email: "", phone: "" });
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Contact</DialogTitle>
          <DialogDescription>
            Someone at the company you work with. Set their roles after adding
            them.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {(["name", "title", "email", "phone"] as const).map((k) => (
            <div key={k} className="flex flex-col gap-1.5">
              <Label htmlFor={`contact-${k}`} className="text-sm capitalize">
                {k}
              </Label>
              <Input id={`contact-${k}`} value={form[k]} onChange={set(k)} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={busy || (!form.name.trim() && !form.email.trim())}
            onClick={() => void submit()}
          >
            {busy ? "Adding…" : "Add Contact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SettingRow: FC<{ title: string; desc: string; children: ReactNode }> = ({
  title,
  desc,
  children
}) => (
  <div className="flex items-start gap-3 border-b border-line-2 py-2.5 last:border-b-0">
    <span className="flex flex-1 flex-col gap-0.5">
      <span className="text-base">{title}</span>
      <span className="text-sm text-fg-3">{desc}</span>
    </span>
    {children}
  </div>
);

export const ContactsTab: FC<{ company: Company }> = ({ company }) => {
  const [addOpen, setAddOpen] = useState(false);
  const logins = useContactLogins(company.id).data ?? {};
  const { portal } = company;
  const setPortal = (change: Partial<CompanyPortalSwitches>, message: string) =>
    void companyActions.save(company, { portal: change }, message);
  const invite = (c: CompanyContact) =>
    void runPortal(
      () => PortalFirmAPI.invite(company.id, c.id),
      `Sent ${c.name} a sign-in link to the ${company.name} HR portal`
    );
  const revoke = (c: CompanyContact) =>
    void runPortal(
      () => PortalFirmAPI.revoke(company.id, c.id),
      `${c.name} can't sign in to the HR portal anymore`
    );

  const saveContacts = (
    contacts: Array<Partial<CompanyContact>>,
    message: string
  ) => companyActions.save(company, { contacts }, message);
  const edit = (id: string, change: Partial<CompanyContact>, message: string) =>
    void saveContacts(
      company.contacts.map((c) => (c.id === id ? { ...c, ...change } : c)),
      message
    );
  const toggleRole = (c: CompanyContact, role: ContactRole) =>
    edit(
      c.id,
      {
        roles: c.roles.includes(role)
          ? c.roles.filter((r) => r !== role)
          : [...c.roles, role]
      },
      `Updated ${c.name}'s roles`
    );

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4"
      data-testid="company-contacts"
    >
      <div className="flex items-center gap-2">
        <span className="flex-1 text-base text-fg-3">
          People at {company.name} who work with you. Roles decide who approves,
          signs and pays; access decides what they see in the HR portal.
        </span>
        <Button size="sm" variant="secondary" onClick={() => setAddOpen(true)}>
          <Plus /> Add Contact
        </Button>
      </div>

      <div
        className="shrink-0 overflow-hidden rounded-lg border border-line-2"
        role="table"
      >
        <div
          className={cn(
            COLS,
            "h-9 border-b border-line-2 bg-fill-1 text-sm font-medium text-fg-3"
          )}
          role="row"
        >
          <span>Contact</span>
          <span>Email</span>
          <span>Roles</span>
          <span>Portal access</span>
          <span>Notices</span>
          <span />
        </div>
        {company.contacts.length ? (
          company.contacts.map((c) => (
            <div
              key={c.id}
              role="row"
              className={cn(
                COLS,
                "h-14 border-b border-line-2 last:border-b-0"
              )}
              data-testid={`contact-${c.id}`}
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <span className="inline-flex size-6.5 shrink-0 items-center justify-center rounded-md bg-fill-2 text-sm font-medium">
                  {initialsOf(c.name || c.email)}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-base font-medium">
                    {c.name}
                  </span>
                  <span className="truncate text-sm text-fg-3">{c.title}</span>
                </span>
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-sm text-fg-3">
                  {c.email || "–"}
                </span>
                {c.portal_access !== "none" ? (
                  <span
                    className="truncate text-sm text-fg-3"
                    data-testid={`login-${c.id}`}
                  >
                    {loginLabel(logins[c.id])}
                  </span>
                ) : null}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex min-w-0 flex-wrap gap-1 text-left"
                    aria-label={`Roles for ${c.name}`}
                  >
                    {c.roles.length ? (
                      c.roles.map((r) => (
                        <Badge
                          key={r}
                          variant={
                            r === "approver" || r === "signatory"
                              ? "default"
                              : "secondary"
                          }
                          className="font-normal"
                        >
                          {ROLE_LABEL[r]}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-fg-3">Add a role</span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel>Roles</DropdownMenuLabel>
                  {CONTACT_ROLES.map((r) => (
                    <DropdownMenuCheckboxItem
                      key={r}
                      checked={c.roles.includes(r)}
                      onCheckedChange={() => toggleRole(c, r)}
                    >
                      {ROLE_LABEL[r]}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Select
                value={c.portal_access}
                onValueChange={(v) =>
                  edit(
                    c.id,
                    { portal_access: v as PortalAccess },
                    `Updated ${c.name}'s portal access`
                  )
                }
              >
                <SelectTrigger
                  size="sm"
                  aria-label={`Portal access for ${c.name}`}
                  className="h-7 w-full text-sm"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PORTAL_ACCESS.map((a) => (
                    <SelectItem key={a} value={a}>
                      {ACCESS_LABEL[a]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Switch
                aria-label={`Case notices for ${c.name}`}
                checked={c.notices.length > 0}
                onCheckedChange={(on) =>
                  edit(
                    c.id,
                    { notices: on ? ALL_NOTICES : [] },
                    on
                      ? `${c.name} gets case notices`
                      : `${c.name} stops getting case notices`
                  )
                }
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    aria-label={`More for ${c.name}`}
                  >
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem
                    disabled={!c.email || c.portal_access === "none"}
                    onSelect={() => invite(c)}
                  >
                    <Mail />
                    {logins[c.id] && logins[c.id].status !== "revoked"
                      ? "Resend sign-in link"
                      : "Invite to HR portal"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={c.portal_access === "none"}
                    onSelect={() =>
                      window.open(
                        portalPreviewUrl(company.id, c.id),
                        "_blank",
                        "noopener"
                      )
                    }
                  >
                    <Eye /> Preview their portal
                  </DropdownMenuItem>
                  {logins[c.id] && logins[c.id].status !== "revoked" ? (
                    <DropdownMenuItem onSelect={() => revoke(c)}>
                      <UserX /> Turn off sign-in
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() =>
                      void saveContacts(
                        company.contacts.filter((x) => x.id !== c.id),
                        `Removed ${c.name}`
                      )
                    }
                  >
                    <Trash2 /> Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))
        ) : (
          <p className="px-4 py-8 text-center text-base text-fg-3">
            No contacts yet.
          </p>
        )}
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <div
          className="rounded-lg border border-line-2 p-3.5"
          data-testid="portal-settings"
        >
          <div className="mb-1 flex items-center gap-2">
            <span className="flex-1 text-base font-medium">HR portal</span>
          </div>
          {PORTAL_SWITCHES.map((row) => (
            <SettingRow key={row.key} title={row.title} desc={row.desc}>
              <Switch
                checked={portal[row.key]}
                aria-label={row.title}
                onCheckedChange={(on) =>
                  setPortal(
                    { [row.key]: on },
                    on ? `${row.title}: on` : `${row.title}: off`
                  )
                }
              />
            </SettingRow>
          ))}
          <div className="flex items-center gap-3 pt-2.5">
            <span className="flex-1 text-base">Requests approved by</span>
            <Select
              value={portal.approver_contact_id ?? AUTO_APPROVER}
              onValueChange={(v) =>
                setPortal(
                  { approver_contact_id: v === AUTO_APPROVER ? null : v },
                  "Updated who approves requests"
                )
              }
            >
              <SelectTrigger
                size="sm"
                className="h-7 w-50 text-sm"
                aria-label="Requests approved by"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AUTO_APPROVER}>
                  Contacts With the Approver Role
                </SelectItem>
                {company.contacts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name || c.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="pt-2 text-sm text-fg-3">
            Contacts sign in with a link sent to their email. Use a
            contact&apos;s menu to invite them or preview what they see.
          </p>
        </div>
        <div className="rounded-lg border border-line-2 p-3.5">
          <div className="mb-1 text-base font-medium">What employees see</div>
          <SettingRow
            title="Their case status, tasks and documents"
            desc="Same client portal as individual clients."
          >
            <Switch disabled checked aria-label="Their case" />
          </SettingRow>
          <SettingRow
            title={`What ${company.name} pays`}
            desc="Off for every company. Employees don't see their employer's fees or spend."
          >
            <Switch disabled checked={false} aria-label="Employer fees" />
          </SettingRow>
          <SettingRow title="Other employees" desc="Never visible.">
            <Switch disabled checked={false} aria-label="Other employees" />
          </SettingRow>
        </div>
      </div>

      <AddContactDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onAdd={(c) =>
          saveContacts([...company.contacts, c], `Added ${c.name || c.email}`)
        }
      />
    </div>
  );
};
